import { UseMutationOptions, useQueryClient } from 'react-query';
import { UseQueryOptions } from 'react-query/types/react/types';
import { AxiosResponse } from 'axios';

import {
  CacheAndStaleTimeForUnlikelyChanged,
  useMutationWithToastError,
  useQueryWithToastError,
} from './queryUtils';
import {
  addNewApptOption,
  deleteApptOption,
  getApptOptions,
  updateApptOption,
} from 'services/appt_options';
import {
  ApptOptionResponseData,
  ApptOptionsFilters,
  ApptOptionsFormValues,
} from 'types/apptOptions';
import { UNSAFE_FILTER_WITH_NO_PAGINATION } from 'hooks/usePaginatedFilters';

const APPT_OPTION_QUERY_KEY = 'apptOptions';

export const useApptOptions = (
  filters: ApptOptionsFilters,
  options?: UseQueryOptions<
    AxiosResponse<ApptOptionResponseData>,
    any,
    ApptOptionResponseData
  >,
) => {
  return useQueryWithToastError(
    [
      APPT_OPTION_QUERY_KEY,
      {
        ...filters,
      },
    ],
    () => getApptOptions(filters),
    {
      ...CacheAndStaleTimeForUnlikelyChanged,
      select: (data) => data.data,
      ...options,
    },
  );
};

/**
 * Loads all appointment options of the active site trust without pagination.
 * Used by the patient booking flow, where the whole list is offered at once.
 */
export const useAllApptOptions = (
  options?: UseQueryOptions<
    AxiosResponse<ApptOptionResponseData>,
    any,
    ApptOptionResponseData
  >,
) => {
  const { data, ...rest } = useApptOptions(
    UNSAFE_FILTER_WITH_NO_PAGINATION,
    options,
  );
  return { data: data?.data, ...rest };
};

export const useMutateUpdateApptOption = (
  options?: UseMutationOptions<any, any, ApptOptionsFormValues>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<ApptOptionsFormValues>(
    [APPT_OPTION_QUERY_KEY],
    (update) => updateApptOption(update),
    {
      onSettled: () => {
        client.invalidateQueries([APPT_OPTION_QUERY_KEY]);
      },
      ...options,
    },
  );
};

export const useMutateCreateApptOption = (
  options?: UseMutationOptions<any, any, ApptOptionsFormValues>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<ApptOptionsFormValues>(
    [APPT_OPTION_QUERY_KEY],
    (params) => addNewApptOption(params),
    {
      onSettled: () => client.invalidateQueries([APPT_OPTION_QUERY_KEY]),
      ...options,
    },
  );
};

export const useMutateDeleteApptOption = (
  options?: UseMutationOptions<any, any, string>,
) => {
  const client = useQueryClient();
  return useMutationWithToastError<string>(
    [APPT_OPTION_QUERY_KEY],
    (params) => deleteApptOption(params),
    {
      onSettled: () => client.invalidateQueries([APPT_OPTION_QUERY_KEY]),
      ...options,
    },
  );
};
